import React, { useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import { COLOR_PRIMARY_2, COLOR_PRIMARY_2_100, COLOR_TEXT_2, COLOR_TEXT_4, COLOR_BORDER, COLOR_BORDER_LIGHT } from 'src/utils/colors';
import {
  getPoiPremiumColor,
  getStaticMapWithPoiHotspots,
  POI_PREMIUM_BUCKETS,
  POI_STATIC_MAP_MAX_PINS,
} from 'src/utils/functions';
import {
  checkIfMarketTrackedAlready,
  formatPoiType,
  formatPremium,
  formatRadius,
  Poi,
  sortPois,
} from './helper';
import { logAmplitudeEvent } from 'src/utils/analytics';

export type { Poi };

export const LockedPremiumCell = ({
  handleOnSampleClick,
}: {
  handleOnSampleClick: (plan: string) => void;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
    <Typography
      variant="body2"
      sx={{ fontWeight: 600, color: COLOR_TEXT_2, filter: 'blur(4px)', userSelect: 'none' }}
    >
      +25%
    </Typography>
    <IconButton onClick={() => handleOnSampleClick('expert')} size="small" sx={{ p: 0, m: 0 }}>
      <LockIcon sx={{ color: COLOR_PRIMARY_2, fontSize: '16px' }} />
    </IconButton>
  </Box>
);

export const HotspotLegend = ({
  isSubmarketAccessAllowed,
  handleOnSampleClick,
}: {
  isSubmarketAccessAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
}) => {
  if (!isSubmarketAccessAllowed) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
        <LockIcon sx={{ color: COLOR_PRIMARY_2, fontSize: '16px' }} />
        <Typography
          variant="body2"
          sx={{ fontSize: '12px', color: COLOR_TEXT_4, cursor: 'pointer' }}
          onClick={() => handleOnSampleClick('expert')}
        >
          Upgrade to see how much guests pay at each hotspot
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mt: 1 }}>
      {POI_PREMIUM_BUCKETS.map(bucket => (
        <Box key={bucket.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box
            sx={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: bucket.color,
              opacity: 0.8,
            }}
          />
          <Typography variant="body2" sx={{ fontSize: '12px', color: COLOR_TEXT_4 }}>
            {bucket.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

interface HotspotAnalysisProps {
  pois: Poi[];
  marketDisplayName: string;
  isSubmarketAccessAllowed: boolean;
  handleOnSampleClick: (plan: string) => void;
  isMobileView: boolean;
  currentMarketID: number;
  setDashboardLayoutView?: (view: string) => void;
}

const HotspotAnalysis = ({
  pois,
  marketDisplayName,
  isSubmarketAccessAllowed,
  handleOnSampleClick,
  isMobileView,
  currentMarketID,
  setDashboardLayoutView,
}: HotspotAnalysisProps) => {
  const mapContainerRef = useRef<HTMLDivElement>(null);

  const sortedPois = useMemo(() => sortPois(pois), [pois]);

  useEffect(() => {
    if (!sortedPois.length) return;
    const mapContainer = mapContainerRef.current;
    if (mapContainer) {
      const height = mapContainer.offsetHeight;
      const width = mapContainer.offsetWidth;
      const mapImageUrl = getStaticMapWithPoiHotspots(
        sortedPois,
        height,
        width,
        !isSubmarketAccessAllowed
      );
      mapContainer.querySelector('img')?.setAttribute('src', mapImageUrl);
    }
  }, [sortedPois, isSubmarketAccessAllowed]);

  useEffect(() => {
    if (!sortedPois.length) return;
    if (checkIfMarketTrackedAlready('hotspot_analysis_viewed', currentMarketID)) return;

    logAmplitudeEvent('hotspot_analysis_viewed', {
      market_name: marketDisplayName,
      market_id: currentMarketID,
      pois_count: sortedPois.length,
    });
  }, [sortedPois]);

  if (!sortedPois.length) return null;

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Box
        ref={mapContainerRef}
        sx={{
          width: '100%',
          height: isMobileView ? 'auto' : '480px',
          aspectRatio: isMobileView ? '1 / 1' : 'auto',
          borderRadius: '16px',
          overflow: 'hidden',
        }}
      >
        <img
          src=""
          alt="Hotspot Analysis Map"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '16px',
          }}
        />
      </Box>

      <HotspotLegend
        isSubmarketAccessAllowed={isSubmarketAccessAllowed}
        handleOnSampleClick={handleOnSampleClick}
      />

      <TableContainer
        sx={{
          mt: 2,
          maxHeight: '420px',
          border: `1px solid ${COLOR_BORDER}`,
          borderRadius: '12px',
        }}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {['#', 'Hotspot', 'Type', 'Active Listings', 'Radius', 'Location Premium'].map(
                (header, i) => (
                  <TableCell
                    key={header}
                    align={i === 3 || i === 4 ? 'right' : 'left'}
                    sx={{
                      fontWeight: 600,
                      fontSize: '13px',
                      color: COLOR_PRIMARY_2,
                      backgroundColor: COLOR_PRIMARY_2_100,
                      borderBottom: `1px solid ${COLOR_PRIMARY_2}33`,
                    }}
                  >
                    {header}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedPois.map((poi, index) => {
              const premium =
                typeof poi.avg_premium_factor === 'number' ? poi.avg_premium_factor : null;
              const isLockedPremium = poi.avg_premium_factor === 'locked';

              return (
                <TableRow
                  key={`${poi.name}-${index}`}
                  hover
                  sx={{
                    '&:hover': { backgroundColor: `${COLOR_PRIMARY_2_100}88` },
                    '& td': { borderBottom: `1px solid ${COLOR_BORDER_LIGHT}` },
                  }}
                >
                  <TableCell>
                    {index < POI_STATIC_MAP_MAX_PINS ? (
                      <Chip
                        label={index + 1}
                        size="small"
                        sx={{
                          backgroundColor: COLOR_PRIMARY_2_100,
                          color: COLOR_PRIMARY_2,
                          fontWeight: 600,
                          fontSize: '12px',
                          height: '24px',
                          minWidth: '32px',
                        }}
                      />
                    ) : (
                      '-'
                    )}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {premium !== null && premium > 0 && (
                        <WhatshotIcon
                          fontSize="small"
                          sx={{ color: COLOR_PRIMARY_2, opacity: 0.7, flexShrink: 0 }}
                        />
                      )}
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, color: COLOR_TEXT_2, fontSize: '14px' }}
                      >
                        {poi.name}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={formatPoiType(poi.poi_type)}
                      size="small"
                      variant="outlined"
                      sx={{
                        fontWeight: 500,
                        fontSize: '12px',
                        borderColor: COLOR_PRIMARY_2_100,
                        color: COLOR_TEXT_2,
                      }}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>
                    {poi.listing_count ?? '-'}
                  </TableCell>
                  <TableCell align="right" sx={{ color: COLOR_TEXT_2, fontWeight: 500 }}>
                    {formatRadius(poi.radius_m)}
                  </TableCell>
                  <TableCell>
                    {isLockedPremium ? (
                      <LockedPremiumCell handleOnSampleClick={handleOnSampleClick} />
                    ) : premium !== null ? (
                      (() => {
                        const premiumColor = getPoiPremiumColor(premium);
                        return (
                          <Chip
                            label={formatPremium(premium)}
                            size="small"
                            sx={{
                              backgroundColor: `${premiumColor}1A`,
                              color: premiumColor,
                              fontWeight: 700,
                              fontSize: '12px',
                              border: `1px solid ${premiumColor}33`,
                            }}
                          />
                        );
                      })()
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {setDashboardLayoutView && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button
            variant="contained"
            onClick={() => setDashboardLayoutView('submarkets')}
            startIcon={<WhatshotIcon sx={{ fontSize: '16px' }} />}
            sx={{
              backgroundColor: COLOR_PRIMARY_2,
              color: 'white',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '14px',
              fontWeight: 600,
              px: 4,
              py: 1,
              '&:hover': {
                backgroundColor: COLOR_PRIMARY_2 + 'CC',
              },
            }}
          >
            See all {sortedPois.length} Hotspots
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default HotspotAnalysis;
